import { useEffect, useState } from "react";
import Lenis from "lenis";
import { motion, AnimatePresence } from "framer-motion";
import {
    Zap, Wrench, Hammer, Paintbrush, Trees, Sparkles, Truck, Home,
    Activity, Radar, Clock, ShieldCheck, Star, ArrowRight, TrendingUp, Radio, ChevronDown,
} from "lucide-react";
import { ForceLogo } from "../ForceLogo";
import AdSpot from "../AdSpot";
import ReferralPromo from "../ReferralPromo";
import HeroVideo from "../HeroVideo";
import ReferralLeaderboard from "../ReferralLeaderboard";
import { useLandingData } from "../../hooks/useLandingData";
import { mediaUrl } from "../../lib/api";

const TAGLINES = [
    "LET THE FORCE ASSIST YOU.",
    "TRUSTED HANDS. ZERO HASSLE. ANY TASK.",
    "POST IT — THE FORCE HANDLES THE REST.",
];

const CATEGORIES = [
    { name: "Electrical", code: "ELEC", icon: Zap },
    { name: "Plumbing", code: "PLMB", icon: Wrench },
    { name: "Carpentry", code: "CARP", icon: Hammer },
    { name: "Painting", code: "PNTG", icon: Paintbrush },
    { name: "Yard Work", code: "YARD", icon: Trees },
    { name: "Cleaning", code: "CLNG", icon: Sparkles },
    { name: "Moving", code: "MOVE", icon: Truck },
    { name: "Roofing", code: "ROOF", icon: Home },
];

const STEPS = [
    { n: "01", title: "Post your task", body: "Describe the job, set your budget and drop a pin on your neighbourhood. 60 seconds, done." },
    { n: "02", title: "Meet verified local pros", body: "Quotes roll in fast from background-checked local specialists ready to help." },
    { n: "03", title: "Approve & pay safely", body: "Payment stays protected and is released only when you sign off on the finished work." },
];

function Metric({ value, suffix = "", label, icon: Icon, testid }) {
    return (
        <div data-testid={testid} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-zinc-950/50 px-4 py-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-orange-500/25 bg-orange-500/10 text-orange-500">
                <Icon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
                <motion.div
                    key={String(value)}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                    className="font-display text-2xl font-black tabular-nums leading-none text-white"
                >
                    {value}{suffix}
                </motion.div>
                <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.1em] leading-snug text-zinc-500">{label}</div>
            </div>
        </div>
    );
}

function Reveal({ children, delay = 0 }) {
    return (
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }} className="h-full">
            {children}
        </motion.div>
    );
}

// LAYOUT 2 — "Grid Operations Hub": a data-forward tactical command bento.
// Shares the {header, rightSlot} contract so the right column holds the guest
// auth card OR the logged-in live jobs map, framed as a command terminal.
export default function Layout2Operations({ header, rightSlot }) {
    const { jobsToday, hiredToday, events, pros } = useLandingData();
    const [ti, setTi] = useState(0);
    const [showAllPros, setShowAllPros] = useState(false);

    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        let raf; const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
        raf = requestAnimationFrame(loop);
        return () => { cancelAnimationFrame(raf); lenis.destroy(); };
    }, []);

    useEffect(() => {
        const id = setInterval(() => setTi((i) => (i + 1) % TAGLINES.length), 3200);
        return () => clearInterval(id);
    }, []);

    const ribbon = events.length ? events : null;

    return (
        <div className="relative min-h-screen bg-force-bg text-white" data-testid="layout-operations">
            {/* Tactical grid backdrop */}
            <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.5]" style={{
                backgroundImage:
                    "linear-gradient(rgba(255,122,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,122,0,0.05) 1px, transparent 1px)",
                backgroundSize: "48px 48px",
                maskImage: "radial-gradient(120% 90% at 70% 0%, #000 30%, transparent 80%)",
            }} />
            <div className="pointer-events-none fixed -top-40 right-0 z-0 h-[520px] w-[520px] rounded-full bg-orange-600/10 blur-[120px]" />

            <div className="relative z-10">
                {header}

                {/* 1 — Top metrics bar */}
                <div className="mx-auto max-w-7xl px-5 sm:px-10 pt-5">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="ops-metrics-bar">
                        <Metric testid="ops-metric-jobs" value={jobsToday ?? "—"} label="Jobs posted" icon={Activity} />
                        <Metric testid="ops-metric-hired" value={hiredToday ?? "—"} label="Hired today" icon={TrendingUp} />
                        <Metric testid="ops-metric-match" value="4.2m" label="Avg match time" icon={Clock} />
                        <Metric testid="ops-metric-verified" value="12.4k" label="Verified pros" icon={ShieldCheck} />
                    </div>
                </div>

                {/* 2 — Hero bento */}
                <section className="mx-auto max-w-7xl px-5 sm:px-10 pt-8 md:pt-12 pb-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Left: headline + rotating tagline */}
                    <div className="lg:col-span-7 flex flex-col justify-start lg:pt-4">
                        <span className="inline-flex w-fit items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-orange-300">
                            <Radio className="h-3.5 w-3.5 animate-pulse" /> Live · Jobs near you
                        </span>
                        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[0.92] tracking-tight">
                            Let the <span className="text-orange-500">FORCE</span><br />
                            assist you.
                        </h1>
                        <div className="mt-5 h-10 sm:h-6 overflow-hidden">
                            <AnimatePresence mode="wait">
                                <motion.p
                                    key={ti}
                                    initial={{ y: 22, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -22, opacity: 0 }}
                                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                    className="font-mono text-xs sm:text-sm uppercase tracking-[0.16em] text-zinc-400"
                                    data-testid="ops-tagline"
                                >
                                    {TAGLINES[ti]}
                                </motion.p>
                            </AnimatePresence>
                        </div>
                        <p className="mt-6 max-w-md text-base text-zinc-300 leading-relaxed">
                            The FORCE connects you with trusted local pros at your door — one verified handshake at a time. Post a job, and consider it handled.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {CATEGORIES.slice(0, 6).map((c) => (
                                <span key={c.code} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-mono uppercase tracking-wide text-zinc-300 hover:border-orange-500/50 hover:text-white transition-colors cursor-default">
                                    {c.name}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Right: command terminal slot (auth card OR live map) */}
                    <div className="lg:col-span-5">
                        <div className="relative rounded-3xl border border-orange-500/25 bg-zinc-950/40 p-3 sm:p-4 backdrop-blur-xl shadow-[0_0_60px_-18px_rgba(255,122,0,0.5)]">
                            {/* HUD corner marks */}
                            <span className="pointer-events-none absolute left-2 top-2 h-4 w-4 border-l-2 border-t-2 border-orange-500/60 rounded-tl" />
                            <span className="pointer-events-none absolute right-2 top-2 h-4 w-4 border-r-2 border-t-2 border-orange-500/60 rounded-tr" />
                            <span className="pointer-events-none absolute left-2 bottom-2 h-4 w-4 border-l-2 border-b-2 border-orange-500/60 rounded-bl" />
                            <span className="pointer-events-none absolute right-2 bottom-2 h-4 w-4 border-r-2 border-b-2 border-orange-500/60 rounded-br" />
                            <div className="mb-2.5 flex items-center justify-between px-1">
                                <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-orange-300"><Radar className="h-3.5 w-3.5" /> Live jobs map</span>
                                <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live</span>
                            </div>
                            <div className="flex justify-center [&>*]:max-w-none [&>*]:w-full">
                                {rightSlot}
                            </div>
                        </div>
                    </div>
                </section>

                <HeroVideo variant="operations" />

                {/* 2.5 — Sponsored business uplink */}
                <section className="mx-auto max-w-7xl px-5 sm:px-10 pb-8">
                    <AdSpot variant="operations" placement="landing" />
                    <ReferralPromo variant="operations" />
                    <ReferralLeaderboard variant="operations" />
                </section>

                {/* 3 — Live hiring telemetry ribbon */}
                {ribbon && (
                    <div className="relative border-y border-white/10 bg-white/5 py-3 overflow-hidden" data-testid="ops-activity-ribbon">
                        <motion.div className="flex whitespace-nowrap gap-6" animate={{ x: ["0%", "-50%"] }} transition={{ duration: 34, ease: "linear", repeat: Infinity }}>
                            {[0, 1].map((k) => (
                                <div key={k} className="flex gap-6 items-center">
                                    {ribbon.map((e, i) => (
                                        <span key={`${k}-${i}`} className="flex items-center gap-2 text-xs font-mono text-zinc-400">
                                            <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
                                            <span className="text-orange-400 font-semibold">{e.who}</span>
                                            <span className="text-zinc-500">·</span> {e.text?.replace(`${e.who} `, "")}
                                            <span className="text-zinc-500">· {e.mins_ago}m ago</span>
                                        </span>
                                    ))}
                                </div>
                            ))}
                        </motion.div>
                    </div>
                )}

                {/* 4 — Service categories matrix */}
                <section className="mx-auto max-w-7xl px-5 sm:px-10 py-16 sm:py-20">
                    <Reveal>
                        <div className="mb-8 flex items-end justify-between">
                            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">Service <span className="text-orange-500">matrix</span></h2>
                            <span className="hidden sm:block font-mono text-xs text-zinc-500">/ 8 TRADE CATEGORIES</span>
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {CATEGORIES.map((c, i) => {
                            const live = 3 + ((i * 7 + (jobsToday || 11)) % 22);
                            return (
                                <Reveal key={c.code} delay={i * 0.04}>
                                    <div data-testid={`ops-cat-${c.code.toLowerCase()}`} className="group relative h-full overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/50 p-4 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-orange-500/50">
                                        <div className="flex items-center justify-between">
                                            <span className="grid h-10 w-10 place-items-center rounded-xl border border-orange-500/25 bg-orange-500/10 text-orange-500 transition-colors group-hover:bg-orange-500 group-hover:text-zinc-950">
                                                <c.icon className="h-5 w-5" />
                                            </span>
                                            <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">{c.code}</span>
                                        </div>
                                        <p className="mt-3 font-display text-base font-bold">{c.name}</p>
                                        <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-zinc-500">
                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {live} pros active
                                        </p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </section>

                {/* 5 — Top pros squadron */}
                {pros.length > 0 && (
                    <section className="mx-auto max-w-7xl px-5 sm:px-10 pb-16 sm:pb-20">
                        <Reveal>
                            <div className="mb-8 flex items-end justify-between">
                                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">Top-rated <span className="text-orange-500">local pros</span></h2>
                                <span className="hidden sm:block font-mono text-xs text-zinc-500">/ RANKED BY JOBS DONE</span>
                            </div>
                        </Reveal>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {pros.slice(0, showAllPros ? pros.length : 3).map((p, i) => (
                                <Reveal key={p.id} delay={i * 0.05}>
                                    <div data-testid={`ops-pro-${i}`} className="group h-full rounded-2xl border border-white/10 bg-zinc-950/50 p-4 transition-[border-color] hover:border-orange-500/50">
                                        <div className="flex items-center gap-3">
                                            <div className="relative">
                                                <img src={mediaUrl(p.avatar_url)} alt={p.name} className="h-12 w-12 rounded-xl object-cover" />
                                                <span className="absolute -right-1 -top-1 grid h-4 w-4 place-items-center rounded-full bg-orange-500 font-mono text-[9px] font-black text-zinc-950">{i + 1}</span>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="flex items-center gap-1 font-semibold text-white truncate">{p.name}{p.verified && <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-orange-400" />}</p>
                                                <p className="text-[11px] text-zinc-500 truncate">{p.specialty} · {(p.city || "").split(",")[0]}</p>
                                            </div>
                                        </div>
                                        <div className="mt-3 flex items-center justify-between border-t border-white/5 pt-3 text-xs">
                                            <span className="flex items-center gap-1 text-amber-300"><Star className="h-3.5 w-3.5 fill-amber-300" />{p.rating}</span>
                                            <span className="font-mono text-zinc-500">{p.jobs_completed} done</span>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                        {pros.length > 3 && (
                            <Reveal delay={0.1}>
                                <button
                                    data-testid="ops-pros-more"
                                    onClick={() => setShowAllPros((v) => !v)}
                                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-orange-500/30 bg-orange-500/5 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-orange-300 transition-colors hover:border-orange-500/60 hover:bg-orange-500/10"
                                >
                                    {showAllPros ? "Show less" : `Show all ${pros.length} pros`}
                                    <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${showAllPros ? "rotate-180" : ""}`} />
                                </button>
                            </Reveal>
                        )}
                    </section>
                )}

                {/* 6 — How it works */}
                <section className="mx-auto max-w-7xl px-5 sm:px-10 pb-20">
                    <Reveal>
                        <h2 className="mb-8 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">How it <span className="text-orange-500">works</span></h2>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.1}>
                                <div className="group relative h-full rounded-3xl border border-white/10 bg-zinc-950/50 p-6 transition-[border-color] hover:border-orange-500/50">
                                    <div className="flex items-center justify-between">
                                        <div className="font-mono text-4xl font-black text-orange-500/20 dark:text-white/10 group-hover:text-orange-500/40 dark:group-hover:text-orange-500/30 transition-colors">{s.n}</div>
                                        <ArrowRight className="h-5 w-5 text-orange-500/60 transition-transform group-hover:translate-x-1" />
                                    </div>
                                    <h3 className="mt-3 font-display text-lg font-bold">{s.title}</h3>
                                    <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </section>

                {/* 7 — Footer */}
                <footer className="border-t border-white/10 px-6 sm:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <ForceLogo size={30} />
                    <p className="text-xs text-zinc-500">© {new Date().getFullYear()} FORCE · Let the FORCE assist you.</p>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">FORCE · LIVE NEAR YOU</span>
                </footer>
            </div>
        </div>
    );
}
